import { supabase } from '../lib/supabase';

export interface PaymentMethod {
  id: string;
  user_id: string;
  type: 'card' | 'bank' | 'paypal' | 'apple' | 'google';
  name: string;
  last4?: string;
  expiry?: string;
  email?: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export class PaymentMethodService {
  /**
   * Get all payment methods for a user
   */
  static async getUserPaymentMethods(userId: string): Promise<PaymentMethod[]> {
    try {
      console.log('PaymentMethodService: Fetching payment methods for user_id:', userId);
      
      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .eq('user_id', userId)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: false });

      console.log('PaymentMethodService: Supabase response:', { data, error });

      if (error) {
        console.error('Error fetching payment methods:', error);
        throw error;
      }

      console.log('PaymentMethodService: Returning payment methods:', data || []);
      return data || [];
    } catch (error) {
      console.error('Error in getUserPaymentMethods:', error);
      throw error;
    }
  }

  /**
   * Add a new payment method
   */
  static async addPaymentMethod(paymentMethod: Omit<PaymentMethod, 'id' | 'created_at' | 'updated_at'>): Promise<PaymentMethod> {
    try {
      const { data, error } = await supabase
        .from('payment_methods')
        .insert([paymentMethod])
        .select()
        .single();

      if (error) {
        console.error('Error adding payment method:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in addPaymentMethod:', error);
      throw error;
    }
  }

  /**
   * Update an existing payment method
   */
  static async updatePaymentMethod(id: string, updates: Partial<PaymentMethod>): Promise<PaymentMethod> {
    try {
      const { data, error } = await supabase
        .from('payment_methods')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating payment method:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in updatePaymentMethod:', error);
      throw error;
    }
  }

  /**
   * Delete a payment method
   */
  static async deletePaymentMethod(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('payment_methods')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting payment method:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deletePaymentMethod:', error);
      throw error;
    }
  }

  /**
   * Set a payment method as default (automatically unsets others)
   */
  static async setDefaultPaymentMethod(id: string, userId: string): Promise<void> {
    try {
      // First, unset all other default payment methods for this user
      await supabase
        .from('payment_methods')
        .update({ is_default: false })
        .eq('user_id', userId)
        .neq('id', id);

      // Then set this one as default
      const { error } = await supabase
        .from('payment_methods')
        .update({ is_default: true })
        .eq('id', id);

      if (error) {
        console.error('Error setting default payment method:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in setDefaultPaymentMethod:', error);
      throw error;
    }
  }

  /**
   * Get the default payment method for a user
   */
  static async getDefaultPaymentMethod(userId: string): Promise<PaymentMethod | null> {
    try {
      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .eq('user_id', userId)
        .eq('is_default', true)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
        console.error('Error fetching default payment method:', error);
        throw error;
      }

      return data || null;
    } catch (error) {
      console.error('Error in getDefaultPaymentMethod:', error);
      throw error;
    }
  }
}
